import { useParams } from 'react-router-dom';
import './module.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUI } from '../../UIContext';
import { get, post } from '../../services/api';
import { handleErr } from '../../utils/functions';
import Icon from '../../components/Icon';
import CheckInput from '../../components/CheckInput';
import Player from '@vimeo/player';

function Module() {
    const { id } = useParams();

    const { setLoading, setMessage } = useUI();
    const [pageLoaded, setPageLoaded] = useState(false);
    const [module, setModule] = useState({});
    const [classes, setClasses] = useState([]);
    const [classeSelected, setClassSelected] = useState({});
    const [showVideoEndedPlaceholder, setShowVideoEndedPlaceholder] = useState(false);
    const videoLoadingRef = useRef(false);
    const videoLoadingTimeoutRef = useRef(null);
    const iframeRef = useRef(null);
    const playerRef = useRef(null);
    const descriptionTextRef = useRef(null);
    const [descriptionHasScroll, setDescriptionHasScroll] = useState(false);

    function clearVideoLoadingTimeout() {
        if (!videoLoadingTimeoutRef.current)
            return;

        clearTimeout(videoLoadingTimeoutRef.current);
        videoLoadingTimeoutRef.current = null;
    }

    const startVideoLoading = useCallback(() => {
        if (videoLoadingRef.current)
            return;

        videoLoadingRef.current = true;
        setLoading(true);

        videoLoadingTimeoutRef.current = setTimeout(() => {
            videoLoadingRef.current = false;
            videoLoadingTimeoutRef.current = null;
            setLoading(false);
        }, 20000);
    }, [setLoading]);

    const stopVideoLoading = useCallback(() => {
        if (!videoLoadingRef.current)
            return;

        clearVideoLoadingTimeout();
        videoLoadingRef.current = false;
        setLoading(false);
    }, [setLoading]);

    useEffect(() => {
        let active = true;

        async function getModule() {
            setLoading(true);

            let loadedVideoProgress = null;

            await get('/video-progress/course', true, setMessage,
                (response) => {
                    loadedVideoProgress = response.data?.videoProgress || null;
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar o progresso do curso: ${err}`);
                },
                () => {},
                'logout',
                'none'
            );

            await get(`module/${id}`, true, setMessage,
                (response) => {
                    if (!active)
                        return;

                    setModule(response.data.module);
                    const responseClasses = Array.isArray(response.data?.classes)
                        ? response.data.classes
                        : [];

                    const moduleClasses = responseClasses.map(element => ({
                        ...element,
                        watched: false
                    }));

                    const progressVideoId = loadedVideoProgress?.videoId;
                    const classFromVideoProgress = progressVideoId
                        ? moduleClasses.find(moduleClass => String(moduleClass?.video?.id) === String(progressVideoId))
                        : null;

                    const classToSelect = classFromVideoProgress || moduleClasses[0] || {};

                    setClasses(moduleClasses);
                    setClassSelected(classToSelect);

                    setPageLoaded(true);

                    if (classToSelect?.video?.url) {
                        startVideoLoading();
                    }
                },
                (err) => {
                    if (!active)
                        return;

                    handleErr(setMessage, `Não foi possível carregar o módulo: ${err}`);
                },
                () => {
                    setLoading(false);
                },
                'redirect',
                'redirect',
                'unauthorized'
            )
        }

        getModule();

        return () => {
            active = false;
        };
    }, [setModule, setLoading, setMessage, id, startVideoLoading]);

    useEffect(() => {
        return () => {
            clearVideoLoadingTimeout();
            stopVideoLoading();
        };
    }, [stopVideoLoading]);

    useEffect(() => {
        const element = descriptionTextRef.current;

        if (!element) {
            setDescriptionHasScroll(false);
            return;
        }

        const syncDescriptionScrollState = () => {
            setDescriptionHasScroll(element.scrollHeight > element.clientHeight + 1);
        };

        syncDescriptionScrollState();
        window.addEventListener('resize', syncDescriptionScrollState);

        return () => {
            window.removeEventListener('resize', syncDescriptionScrollState);
        };
    }, [classeSelected?.description]);

    const sendProgress = useCallback(async (seconds) => {
        const player = playerRef.current;
        if (!player)
            return;

        const duration = await player.getDuration();

        const data = {
            videoId: classeSelected.video?.id || 0,
            duration: Math.floor(duration),
            lastSecond: Math.floor(seconds)
        };

        await post('video-progress', data, true, setMessage,
            () => { },
            (err) => {
                console.log(`Falha ao registrar o progresso do vídeo: ${err}`);
            }
        );
    }, [classeSelected, setMessage]);

    useEffect(() => {
        if (!classeSelected?.video?.url || !iframeRef.current)
            return;

        const player = new Player(iframeRef.current);
        playerRef.current = player;

        let interval = null;

        const startInterval = () => {
            if (!interval) {
                interval = setInterval(async () => {
                    if (playerRef.current) {
                        const seconds = await playerRef.current.getCurrentTime();
                        await sendProgress(seconds);
                    }
                }, 10000);
            }
        };

        const stopInterval = () => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }
        };

        player.on('play', () => {
            startInterval();
        });

        player.on('pause', async () => {
            stopInterval();
            const seconds = await player.getCurrentTime();
            await sendProgress(seconds);
        });

        player.on('ended', async () => {
            stopInterval();
            await sendProgress('COMPLETED');

            setClasses(prevClasses => {
                return prevClasses.map(c =>
                    c.id === classeSelected.id
                        ? { ...c, videoProgress: { ...c.videoProgress, completed: true } }
                        : c
                );
            });

            setTimeout(() => {
                setClasses(prevClasses => {
                    const idx = prevClasses.findIndex(c => c.id === classeSelected.id);
                    if (idx !== -1 && idx + 1 < prevClasses.length) {
                        setClassSelected(prevClasses[idx + 1]);
                        setShowVideoEndedPlaceholder(false);
                    } else {
                        setShowVideoEndedPlaceholder(true);
                    }
                    return prevClasses;
                });
            }, 500);
        });

        return () => {
            stopInterval();
            player.destroy();
        };
    }, [classeSelected, sendProgress]);
    
    const handleVideoLoad = async () => {
        stopVideoLoading();

        if (
            classeSelected?.video?.id &&
            typeof classeSelected?.videoProgress?.lastSecond === 'number' &&
            playerRef.current &&
            !classeSelected?.videoProgress?.completed
        ) {
            try {
                await playerRef.current.setCurrentTime(classeSelected.videoProgress.lastSecond);
            } catch (e) {
                console.warn('Falha ao sincronizar tempo do vídeo:', e);
            }
        }
    };

    const handleVideoError = () => {
        stopVideoLoading();
        handleErr(setMessage, 'Não foi possível carregar o vídeo da aula.');
    };

    function onClickClass(moduleClass) {
        if (moduleClass?.id === classeSelected?.id)
            return;

        setShowVideoEndedPlaceholder(false);

        if (moduleClass?.video?.url) {
            startVideoLoading();
        } else {
            stopVideoLoading();
        }

        setClassSelected(moduleClass);
    }

    if (!pageLoaded)
        return;

    const hasMaterial = typeof module?.materialPath === 'string' && module.materialPath.trim().length > 0;
    const materialButtonClassName = hasMaterial
        ? 'blue-button zoom-effect download-material'
        : 'blue-button download-material disabled-button';

    return (
        <div className='main-container-module'>
            <div className='glass-bg-full-page'></div>
            <h1>{module.name}</h1>

            <div className='container-module-data'>
                <div className='container-module-main'>
                    <div className='container-white container-module-class'>
                        <div className='container-video-preview'>
                            {showVideoEndedPlaceholder ? (
                                <div className='video-ended-placeholder'></div>
                            ) : (
                                <iframe
                                    ref={iframeRef}
                                    key={classeSelected?.id || classeSelected?.video?.url}
                                    src={classeSelected.video?.url}
                                    allow="fullscreen; picture-in-picture"
                                    title={classeSelected.name}
                                    onLoad={handleVideoLoad}
                                    onError={handleVideoError}>
                                </iframe>
                            )}
                        </div>
                    </div>

                    <div className='container-white container-module-description'>
                        <h2>Descrição da Aula</h2>

                        <p
                            ref={descriptionTextRef}
                            className={`module-class-description-text ${descriptionHasScroll ? 'has-scroll' : ''}`}>
                            {classeSelected.description}
                        </p>

                        <button
                            className={materialButtonClassName}
                            disabled={!hasMaterial}
                            onClick={() => window.open(`/material/${id}`, '_blank', 'noopener,noreferrer')}>
                            <Icon iconClass={'icon-white-download'} />
                            <p>{hasMaterial ? 'Visualizar material de apoio' : 'Não há material para visualizar'}</p>
                        </button>
                    </div>
                </div>

                <div className='container-white container-module-classes'>
                    <h2>Neste Módulo</h2>
                    <ul>
                        {classes.map((item, idx) => (
                            <li
                                key={item.id}
                                className={ item.id === classeSelected.id ? 'class-selected' : ''}
                                onClick={() => onClickClass(item)}
                            >
                                <CheckInput value={item.videoProgress?.completed || false} />
                                {`${idx + 1}. ${item.name}`}
                            </li>
                        ))}
                    </ul>
                </div>

            </div>
        </div>
            
    );
}

export default Module;